import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCardIcon, ArrowPathIcon, DocumentTextIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { contractsService } from '@/services/contractsService';
import type { StripeBillingSummary } from '@/types/stripe';
import { StripeSubscriptionCard } from '@/components/stripe/StripeSubscriptionCard';
import { StripeDefaultPaymentMethodCard } from '@/components/stripe/StripeDefaultPaymentMethodCard';
import { StripeInvoicesTable } from '@/components/stripe/StripeInvoicesTable';
import { StripeTransactionsTable } from '@/components/stripe/StripeTransactionsTable';
import { ManageBillingButton } from '@/components/stripe/ManageBillingButton';
import { StripeSkeleton } from '@/components/stripe/StripeSkeleton';

type StripeUiErrorKind = 'not_found' | 'not_configured' | 'stripe_error' | 'unknown';

interface StripeUiError {
  kind: StripeUiErrorKind;
  message: string;
}

interface StripeBillingSectionProps {
  hiringCode: string;
}

type HttpErrorLike = {
  response?: { status?: number; data?: { detail?: unknown } };
  message?: unknown;
};

function parseStripeError(err: unknown): StripeUiError {
  const e = err as HttpErrorLike;
  const status = typeof e?.response?.status === 'number' ? e.response.status : undefined;
  const detail = e?.response?.data?.detail ?? e?.message;
  const detailStr = typeof detail === 'string' ? detail : JSON.stringify(detail);
  const detailLower = detailStr.toLowerCase();

  if (status === 404) {
    const isContractMissing =
      detailLower.includes('contrato') ||
      detailLower.includes('contract') ||
      detailLower.includes('no encontrado') ||
      detailLower.includes('not found');
    return {
      kind: 'not_found',
      message: isContractMissing ? 'contract_not_found' : 'no_info',
    };
  }

  if (
    status === 500 &&
    (detailLower.includes('stripe no está configurado') ||
      detailLower.includes('stripe no esta configurado') ||
      detailLower.includes('stripe not configured') ||
      (detailLower.includes('stripe') && detailLower.includes('config')))
  ) {
    return { kind: 'not_configured', message: 'not_configured' };
  }

  if (status === 502) {
    return { kind: 'stripe_error', message: 'query_error' };
  }

  return { kind: 'unknown', message: detailStr || 'load_error' };
}

export function StripeBillingSection({ hiringCode }: StripeBillingSectionProps) {
  const { t } = useTranslation('billing');
  const [summary, setSummary] = useState<StripeBillingSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<StripeUiError | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await contractsService.getStripeBillingSummary(hiringCode);
      setSummary(data);
    } catch (err: unknown) {
      setSummary(null);
      setError(parseStripeError(err));
    } finally {
      setLoading(false);
    }
  }, [hiringCode]);

  useEffect(() => {
    if (hiringCode) load();
  }, [hiringCode, load]);

  const subscription = useMemo(() => summary?.subscription ?? null, [summary]);
  const customer = useMemo(() => summary?.customer ?? null, [summary]);
  const paymentMethod = useMemo(() => summary?.default_payment_method ?? null, [summary]);

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle className="flex items-center gap-2">
          <CreditCardIcon width={20} height={20} />
          {t('stripe.title')}
        </CardTitle>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}>
          <ArrowPathIcon width={16} height={16} className="mr-2" />
          {t('stripe.reload')}
        </Button>
      </CardHeader>

      <CardContent className="space-y-6">
        {loading && <StripeSkeleton />}

        {!loading && error && (
          <div
            className={
              error.kind === 'stripe_error' || error.kind === 'unknown'
                ? 'rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700'
                : 'rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700'
            }
          >
            {t(`stripe.${error.message}`, error.message)}
            <div className="mt-2">
              <Button size="sm" variant="outline" onClick={load}>
                {t('stripe.retry')}
              </Button>
            </div>
          </div>
        )}

        {!loading && !error && !summary && (
          <div className="text-sm text-gray-600">{t('stripe.noInfo')}</div>
        )}

        {!loading && !error && summary && (
          <div className="space-y-6">
            {/* Suscripción */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('stripe.subscription')}</h3>
              <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                <StripeSubscriptionCard subscription={subscription} />
                {subscription?.id && (
                  <a
                    href={`https://dashboard.stripe.com/subscriptions/${subscription.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-green-600 hover:text-green-700 inline-flex items-center gap-1 mt-2"
                  >
                    {t('stripe.viewInStripe')}
                  </a>
                )}
              </div>
            </section>

            {/* Método de pago */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('stripe.paymentMethod')}</h3>
              <StripeDefaultPaymentMethodCard paymentMethod={paymentMethod} />
              {customer?.id && (
                <p className="text-xs text-gray-500 mt-1">{t('stripe.customer', { id: customer.id })}</p>
              )}
            </section>

            {/* Botón Portal - full width prominente */}
            <ManageBillingButton
              hiringCode={hiringCode}
              className="w-full min-h-[48px] bg-green-600 hover:bg-green-700 text-white font-medium"
            />

            {/* Últimas facturas */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <DocumentTextIcon width={18} height={18} />
                {t('stripe.latestInvoices')}
              </h3>
              <StripeInvoicesTable invoices={summary.invoices} />
            </section>

            {/* Transacciones */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <BanknotesIcon width={18} height={18} />
                {t('stripe.transactions')}
              </h3>
              <StripeTransactionsTable transactions={summary.transactions} />
            </section>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

